import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SOCLOSE } from "../constants/soclose";
import { useT } from "../i18n";
import { Icon } from "./Icon";

const STORAGE_KEY = "soclose_cookie_consent_v1";

export type CookiePrefs = { essential: boolean; analytics: boolean; marketing: boolean; ts?: number };

// PDPA-compliant cookie consent banner. Distinguishes essential / analytics / marketing cookies
// and stores the granular choice so it can be re-checked via getCookieConsent().
export function CookieConsent({ privacyPath = "/privacy", cookiesPath = "/cookies" }: { privacyPath?: string; cookiesPath?: string }) {
  const tr = useT();
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState<CookiePrefs>({ essential: true, analytics: false, marketing: false });

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) setVisible(true);
      else setPrefs({ ...(JSON.parse(stored) as CookiePrefs), essential: true });
    } catch { setVisible(true); }
  }, []);

  const save = (choice: { analytics: boolean; marketing: boolean }) => {
    const payload: CookiePrefs = { ...choice, essential: true, ts: Date.now() };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch { /* ignore */ }
    setPrefs(payload);
    setVisible(false);
    window.dispatchEvent(new CustomEvent("soclose:cookie-consent", { detail: payload }));
  };
  const acceptAll = () => save({ analytics: true, marketing: true });
  const rejectAll = () => save({ analytics: false, marketing: false });
  const saveCurrent = () => save({ analytics: prefs.analytics, marketing: prefs.marketing });

  if (!visible) return null;

  return (
    <div role="dialog" aria-label="Cookie consent" aria-describedby="cookie-consent-text" className="cookie-wrap">
      <div className="cookie-card">
        {!showSettings ? (
          <>
            <div className="cookie-head"><Icon name="shield" size={16} /> <h2>{tr("Cookies & confidentialité")}</h2></div>
            <p id="cookie-consent-text" className="cookie-text">
              {tr("Nous utilisons des cookies pour sécuriser le site, comprendre son usage et améliorer nos services. Les cookies essentiels sont toujours actifs. Vos données sont traitées conformément à la")}{" "}
              <Link to={privacyPath}>{tr("Politique de confidentialité")}</Link> {tr("de")} <strong>{SOCLOSE.name}</strong>{" "}
              ({SOCLOSE.uenLabel}, {tr("Singapour")}), {tr("au titre du")} {SOCLOSE.privacy.frameworkFull}.
            </p>
            <div className="cookie-actions">
              <button className="cookie-link" onClick={() => setShowSettings(true)}>{tr("Personnaliser")}</button>
              <div className="row wrap" style={{ gap: 8 }}>
                <button className="btn ghost sm" onClick={rejectAll}>{tr("Refuser le non-essentiel")}</button>
                <button className="btn primary sm" onClick={acceptAll}>{tr("Tout accepter")}</button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="cookie-head"><Icon name="shield" size={16} /> <h2>{tr("Préférences cookies")}</h2></div>
            <div className="cookie-rows">
              <Row title={tr("Essentiels")} desc={tr("Nécessaires au fonctionnement (session, sécurité).")} enabled disabled />
              <Row title="Analytics" desc={tr("Mesure d'audience anonymisée pour améliorer le service.")} enabled={prefs.analytics} onChange={(v) => setPrefs({ ...prefs, analytics: v })} />
              <Row title="Marketing" desc={tr("Personnalisation et mesure des campagnes.")} enabled={prefs.marketing} onChange={(v) => setPrefs({ ...prefs, marketing: v })} />
            </div>
            <div className="cookie-actions">
              <Link to={cookiesPath} className="cookie-link">{tr("Politique cookies")}</Link>
              <div className="row wrap" style={{ gap: 8 }}>
                <button className="btn ghost sm" onClick={() => setShowSettings(false)}>{tr("Retour")}</button>
                <button className="btn primary sm" onClick={saveCurrent}>{tr("Enregistrer")}</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ title, desc, enabled, disabled, onChange }: { title: string; desc: string; enabled: boolean; disabled?: boolean; onChange?: (v: boolean) => void }) {
  return (
    <div className="cookie-row">
      <div><div className="cookie-row-t">{title}</div><div className="cookie-row-d">{desc}</div></div>
      <button type="button" onClick={() => !disabled && onChange?.(!enabled)} disabled={disabled} aria-pressed={enabled}
        className={`cookie-switch${enabled ? " on" : ""}${disabled ? " dis" : ""}`}><span /></button>
    </div>
  );
}

// Read current consent elsewhere in app (e.g. to gate analytics init).
export function getCookieConsent(): CookiePrefs | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CookiePrefs) : null;
  } catch { return null; }
}
